import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

const colors = {
  rubiks: {
    white: '#FFFFFF',
    yellow: '#FFD500',
    red: '#B71234',
    orange: '#FF5800',
    green: '#009B48',
    blue: '#0046AD',
    black: '#1A202C',
  },
  brand: {
    50: '#FFF1E5',
    100: '#FFE0CC',
    200: '#FFB999',
    300: '#FF8F66',
    400: '#FF6B33',
    500: '#FF4D00', // Keymash orange
    600: '#CC3D00',
    700: '#992E00',
    800: '#661F00',
    900: '#330F00',
  },
  keymash: {
    bg: {
      base: '#0A0A0B',
      card: '#141417',
      elevated: '#1A1A1F',
      highlight: '#22222A',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#A1A1AA',
      muted: '#71717A',
    },
    border: {
      subtle: '#27272A',
      strong: '#3F3F46',
    },
    accent: {
      blue: '#60A5FA',
      purple: '#A78BFA',
      pink: '#F472B6',
    },
  },
};

const semanticTokens = {
  colors: {
    'bg.default': {
      default: 'gray.50',
      _dark: 'keymash.bg.base',
    },
    'bg.subtle': {
      default: 'gray.100',
      _dark: 'keymash.bg.card',
    },
    'bg.muted': {
      default: 'gray.200',
      _dark: 'keymash.bg.elevated',
    },
    'bg.emphasized': {
      default: 'white',
      _dark: 'keymash.bg.highlight',
    },
    'text.default': {
      default: 'gray.900',
      _dark: 'keymash.text.primary',
    },
    'text.muted': {
      default: 'gray.600',
      _dark: 'keymash.text.secondary',
    },
    'text.subtle': {
      default: 'gray.500',
      _dark: 'keymash.text.muted',
    },
    'border.default': {
      default: 'gray.200',
      _dark: 'keymash.border.subtle',
    },
    'border.emphasized': {
      default: 'gray.300',
      _dark: 'keymash.border.strong',
    },
    accent: {
      default: 'brand.500',
      _dark: 'brand.500',
    },
  },
};

const fonts = {
  heading: "'Rubik', sans-serif",
  body: "'Inter', sans-serif",
};

const components = {
  Button: {
    baseStyle: {
      fontWeight: 'bold',
      borderRadius: 'lg',
      transition: 'all 0.2s',
    },
    variants: {
      solid: (props: { colorScheme: string }) => ({
        bg: `${props.colorScheme}.500`,
        color: 'white',
        _hover: {
          bg: `${props.colorScheme}.600`,
          transform: 'translateY(-1px)',
        },
        _active: {
          bg: `${props.colorScheme}.700`,
          transform: 'translateY(0)',
        },
      }),
      ghost: {
        _hover: {
          bg: 'bg.subtle',
        },
      },
      keymash: {
        bg: 'keymash.bg.elevated',
        color: 'keymash.text.primary',
        borderWidth: '1px',
        borderColor: 'keymash.border.subtle',
        _hover: {
          bg: 'keymash.bg.highlight',
          borderColor: 'keymash.border.strong',
          transform: 'translateY(-1px)',
        },
        _active: {
          transform: 'translateY(0)',
        },
      },
    },
  },
  Card: {
    baseStyle: {
      container: {
        borderRadius: 'xl',
        bg: 'bg.subtle',
        borderWidth: '1px',
        borderColor: 'border.default',
        overflow: 'hidden',
        transition: 'all 0.2s',
        _hover: {
          transform: 'translateY(-2px)',
          boxShadow: 'lg',
        },
      },
    },
  },
  Heading: {
    baseStyle: {
      color: 'text.default',
    },
    variants: {
      gradient: {
        bgGradient: 'linear(to-r, brand.500, keymash.accent.purple, keymash.accent.pink)',
        bgClip: 'text',
        fontWeight: 'extrabold',
      },
    },
  },
  Link: {
    baseStyle: {
      transition: 'all 0.2s',
      _hover: {
        textDecoration: 'none',
        color: 'accent',
      },
    },
  },
};

const config: ThemeConfig = {
  initialColorMode: 'system',
  useSystemColorMode: true,
};

const styles = {
  global: (props: { colorMode: 'light' | 'dark' }) => ({
    body: {
      bg: 'bg.default',
      color: 'text.default',
    },
    '::-webkit-scrollbar': {
      width: '10px',
    },
    '::-webkit-scrollbar-track': {
      bg: 'bg.default',
    },
    '::-webkit-scrollbar-thumb': {
      bg: 'border.default',
      borderRadius: 'full',
    },
  }),
};

const theme = extendTheme({
  colors,
  fonts,
  components,
  config,
  styles,
  semanticTokens,
});

export default theme;